import React, { useEffect, useState, useRef } from "react";
import GoogleLogin from "react-google-login";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import ReactPixel from "react-facebook-pixel";
import { toast } from "react-toastify";
import { useCookies } from "react-cookie";
import {
  X,
  Eye,
  EyeOff,
  ArrowRight,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Check,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Lock,
  Trash2,
  ArrowLeft,
  Info,
  HelpCircle,
  FileText,
  AlertCircle,
  ExternalLink,
  LogOut,
  User,
  Sparkles,
  LifeBuoy,
  MessageCircle,
} from "lucide-react";

import { XANO_GOOGLE_CLIENT_ID } from "../../config/api_config";
import {
  getXanoAuthEmailToken,
  getXanoAuthToken,
  getXanoAuthTokenFromUrl,
  xanoUserSignup,
  logout,
} from "../../redux/actions/authentication";
import { deleteUser } from "../../redux/actions/profileDetails";
import { Mixpanel } from "../../config/Mixpanel";
import Constants from "../../common/constants";
import PasswordResetModal from "../LandingPage25/PasswordResetModal";
import { allFooterLinks } from "../../components/Footer/links";
import "./DeletePage.css";
import { SET_IS_DELETE_ACCOUNT } from "../../redux/actions/types";

const DELETION_REASONS = [
  "Privacy & data security concerns",
  "Too many emails or notifications",
  "I achieved my learning goals",
  "Found an alternative learning platform",
  "Courses are too difficult or too easy",
  "It's too expensive / budget constraints",
  "Other reason",
];

const POLICY_FAQS = [
  {
    question: "What happens to my certificates and learning progress?",
    answer:
      "Upon deleting your account, all completed courses, earned certificates, test scores, and learning hours are permanently erased. Your public certificate verification URLs will become invalid and cannot be restored.",
  },
  {
    question: "What happens to active subscriptions or purchased credits?",
    answer:
      "In accordance with our Refunds & Cancellations Policy, any remaining period on LearnTube Pro, X, or Max plans as well as unspent doubts or credits are forfeited upon account deletion. If eligible within 3 days of purchase, please contact hello@careerninja.in prior to deleting.",
  },
  {
    question: "How is my personal data handled under GDPR & Indian IT laws?",
    answer:
      "Per Section 10 of our Privacy Policy and relevant data protection laws, all Personally Identifiable Information (PII) including name, email, phone, and login credentials will be permanently expunged from active databases within 30 days. Community posts or public forum contributions are irreversibly anonymized.",
  },
  {
    question: "Can I reactivate or recover my account later?",
    answer:
      "No. Account deletion is permanent and irreversible. Once confirmed, you will need to register as a new learner if you choose to return to LearnTube in the future.",
  },
];

export default function DeletePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [cookies, setCookie, removeCookie] = useCookies();

  // Redux auth & user state
  const user_token = useSelector((store) => store?.authentication?.user_token);
  const profileDetails = useSelector((store) => store?.profileDetails);
  const isLoggedIn = Boolean(user_token || cookies?.authToken);

  // Authentication states (for unauthenticated users - based on Login.jsx)
  const initialMode = searchParams.get("mode") === "signup" ? "signup" : "login";
  const [authMode, setAuthMode] = useState(initialMode);
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordResetModalEnabled, setIsPasswordResetModalEnabled] = useState(false);
  const [isEmailError, setIsEmailError] = useState(false);

  const [signupData, setSignupData] = useState({
    name: "",
    email: searchParams.get("email") || "",
    password: "",
  });

  const [loginData, setLoginData] = useState({
    email: searchParams.get("email") || "",
    password: "",
  });

  // Deletion states (for authenticated users - based on DeletionModal.jsx)
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [deletion_reason, setDeletion_reason] = useState("");
  const [confirmationKeyword, setConfirmationKeyword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [countDown, setCountDown] = useState(5);
  const [activeFaq, setActiveFaq] = useState(0);

  const countdownIntervalRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("savedLandingPageURL", window.location.href);
    try {
      ReactPixel.pageView();
    } catch (err) {
      // ignore
    }
  }, []);

  // Post-deletion countdown redirect
  useEffect(() => {
    if (confirmDeletion) {
      countdownIntervalRef.current = setInterval(() => {
        setCountDown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            performLogoutRedirect();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [confirmDeletion]);

  const performLogoutRedirect = () => {
    dispatch(logout());
    removeCookie("authToken", { path: "/" });
    navigate("/courses");
  };

  // Handle auto-login via lt_code / wa_code URL parameters
  // useEffect(() => {
  //   if (searchParams.get("lt_code")) {
  //     toast.success("Logging in Please Wait!");
  //     dispatch(
  //       getXanoAuthTokenFromUrl(
  //         "ltcode",
  //         searchParams.get("lt_code"),
  //         (successObj) => {
  //           if (successObj?.show_toast) {
  //             toast.error(successObj?.message);
  //           } else if (!successObj?.["number_verification"]) {
  //             navigate("/verification");
  //           } else if (
  //             searchParams.get("redirect") &&
  //             searchParams.get("redirect") === "/verification" &&
  //             successObj?.["number_verification"]
  //           ) {
  //             navigate("/courses");
  //           } else if (searchParams.get("redirect")) {
  //             navigate(`${searchParams.get("redirect")}`, { replace: true });
  //           } else {
  //             navigate("/delete");
  //           }
  //         }
  //       )
  //     );
  //   }
  //   if (searchParams.get("wa_code")) {
  //     toast.success("Logging in Please Wait!");
  //     dispatch(
  //       getXanoAuthTokenFromUrl(
  //         "wacode",
  //         searchParams.get("wa_code"),
  //         (successObj) => {
  //           if (successObj?.show_toast) {
  //             toast.error(successObj?.message);
  //           } else if (!successObj?.["number_verification"]) {
  //             navigate("/verification");
  //           } else if (
  //             searchParams.get("redirect") &&
  //             searchParams.get("redirect") === "/verification" &&
  //             successObj?.["number_verification"]
  //           ) {
  //             navigate("/courses");
  //           } else if (searchParams.get("redirect")) {
  //             navigate(`${searchParams.get("redirect")}`, { replace: true });
  //           } else {
  //             navigate("/delete");
  //           }
  //         }
  //       )
  //     );
  //   }
  // }, [dispatch, navigate, searchParams]);

  // Unified post-authentication success handler
  const handleAuthSuccess = (successObj) => {
    try {
      Mixpanel.identify(successObj?.email);
      Mixpanel.track(Constants.Mixpanel.LOG_IN, {
        distinct_id: successObj?.email,
      });
      Mixpanel.people.set({
        $first_name: successObj?.first_name,
        $last_name: successObj?.last_name,
        $email: successObj?.email,
        $name: successObj?.name,
      });
    } catch (e) {
      // ignore
    }

    if (successObj?.number_verification) {

    } else {
      if (successObj?.show_toast) {
        toast.error(successObj?.message);
        if (successObj?.message === "Email already in use") {
          setIsEmailError(true);
        }
        setIsLoadingAuth(false);
      } else {
        dispatch({ type: SET_IS_DELETE_ACCOUNT, payload: true });
        navigate("/verification");
      }
    }
  };

  // Google OAuth
  const handleGoogleAuthResponse = async (response) => {
    if (response.error || (response.status && response.status === "unknown")) {
      console.log("Auth Failed: ", response.error);
      setIsLoadingAuth(false);
    } else {
      const tokenID = response?.tokenObj?.access_token;
      setIsLoadingAuth(true);
      await dispatch(
        getXanoAuthToken(
          tokenID,
          (successObj) => {
            handleAuthSuccess(successObj);
          },
          (err) => {
            setIsLoadingAuth(false);
            toast.error(err?.message || "Google authentication failed");
          }
        )
      );
    }
  };

  // Signup Submit
  const handleSignupSubmit = async (e) => {
    e?.preventDefault();
    setIsEmailError(false);

    if (!signupData.name.trim()) {
      return toast.error('"Full Name" field is required');
    }
    if (!signupData.email.trim()) {
      return toast.error('"Email" field is required');
    }
    if (!signupData.password.trim()) {
      return toast.error('"Password" field is required');
    }

    setIsLoadingAuth(true);
    await dispatch(
      xanoUserSignup(
        {
          name: signupData.name.trim(),
          email: signupData.email.trim(),
          phone: "",
          password: signupData.password,
        },
        (successObj) => {
          handleAuthSuccess(successObj);
        },
        (errObj) => {
          if (errObj?.message === "Email already in use") {
            setIsEmailError(true);
          }
          toast.error(errObj?.message || "Signup failed. Please try again.");
          setIsLoadingAuth(false);
        }
      )
    );
  };

  // Login Submit
  const handleLoginSubmit = async (e) => {
    e?.preventDefault();
    if (!loginData.email.trim()) {
      return toast.error('"Email" field is required');
    }
    if (!loginData.password.trim()) {
      return toast.error('"Password" field is required');
    }

    setIsLoadingAuth(true);
    await dispatch(
      getXanoAuthEmailToken(
        loginData.email.trim(),
        loginData.password,
        (successObj) => {
          handleAuthSuccess(successObj);
        },
        (errorObj) => {
          setIsLoadingAuth(false);
          if (errorObj?.response?.data?.message === "Invalid password syntax.") {
            toast.error("Try Login with Google");
          } else {
            toast.error(
              errorObj?.response?.data?.message || "Invalid credentials provided."
            );
          }
        }
      )
    );
  };

  // Delete Action Handler (from DeletionModal.jsx)
  const handleDeleteAccount = () => {
    if (confirmationKeyword.trim().toLowerCase() !== "delete") {
      toast.error("Enter Delete Keyword");
      return;
    }

    if (!deletion_reason.trim()) {
      toast.error("Enter reason for deletion");
      return;
    }

    setIsDeleting(true);

    dispatch(
      deleteUser(
        deletion_reason.trim(),
        (successObj) => {
          setIsDeleting(false);
          setConfirmDeletion(true);
          setCountDown(5);
        },
        (errorObj) => {
          setIsDeleting(false);
          const errText =
            typeof errorObj === "string"
              ? errorObj
              : errorObj?.message || errorObj?.response?.data?.message || "Failed to delete account. Please try again.";
          toast.error(errText);
        }
      )
    );
  };

  const handleLogout = () => {
    dispatch(logout());
    removeCookie("authToken", { path: "/" });
    toast.info("Logged out successfully");
  };

  // Fallback avatar initial
  const userDisplayName =
    profileDetails?.name ||
    profileDetails?.first_name ||
    (profileDetails?.email ? profileDetails.email.split("@")[0] : "Learner");
  const userEmail = profileDetails?.email || "Account on file";

  return (
    <div className="min-h-screen bg-[#F4F7FB] text-[#0E1B2E] font-sans antialiased selection:bg-red-500 selection:text-white pb-20">
      {/* Top Banner & Header */}
      {/* <header className="border-b border-[#DDE4ED] bg-white sticky top-0 z-30 shadow-xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-[#EDF1F7] text-[#4A5A70] hover:text-[#0E1B2E] transition-colors cursor-pointer"
              title="Go back"
            >
              <ArrowLeft size={18} />
            </button>
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-[30px] h-[30px] rounded-[8px] bg-[#203C5C] flex items-center justify-center overflow-hidden">
                <img
                  src="https://assets.learntube.ai/files/new%20Linkedin%20data/image%2011.png?updatedAt=1747309992449"
                  alt="LearnTube"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-bold text-[17px] text-[#0E1B2E] tracking-tight">
                LearnTube<span className="text-[#13A0BD]">.ai</span>
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-3 text-xs sm:text-sm">
            <Link
              to="/termsandconditions"
              className="text-[#4A5A70] hover:text-[#086788] font-semibold transition-colors hidden sm:inline-block"
            >
              Terms
            </Link>
            <Link
              to="/privacypolicy"
              className="text-[#4A5A70] hover:text-[#086788] font-semibold transition-colors hidden sm:inline-block"
            >
              Privacy
            </Link>
            <Link
              to="/refundpolicy"
              className="text-[#4A5A70] hover:text-[#086788] font-semibold transition-colors hidden sm:inline-block"
            >
              Refund Policy
            </Link>

            {isLoggedIn ? (
              <div className="flex items-center gap-2 pl-3 border-l border-[#DDE4ED]">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#13A0BD] to-[#086788] text-white flex items-center justify-center font-bold text-xs uppercase shadow-xs">
                  {userDisplayName.charAt(0)}
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="p-1.5 text-[#78899F] hover:text-[#E06064] hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                  title="Sign out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setAuthMode(authMode === "login" ? "signup" : "login")}
                  className="px-3 py-1.5 rounded-full border border-[#13A0BD] text-[#086788] font-bold text-xs hover:bg-[#E6F4F8] transition-colors cursor-pointer"
                >
                  {authMode === "login" ? "Create Account" : "Log In"}
                </button>
              </div>
            )}
          </div>
        </div>
      </header> */}

      {/* Main Container */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        {/* Breadcrumb & Section Header */}
        <div className="mb-8">


          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0E1B2E] tracking-tight flex items-center gap-2.5">
                <ShieldAlert className="text-[#E06064]" size={28} />
                Account & Data Deletion
              </h1>
              <p className="text-sm sm:text-base text-[#4A5A70] mt-1 max-w-2xl">
                Manage your privacy preferences, review data retention rules, and permanently close your LearnTube profile.
              </p>
            </div>

            {/* Quick Policy Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <ShieldCheck size={13} />
                GDPR & DPDP Compliant
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                <AlertTriangle size={13} />
                Irreversible Action
              </span>
            </div>
          </div>
        </div>

        {/* Content Grid: 2 Columns on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* ========================================================================= */}
          {/* LEFT COLUMN: INTERACTIVE FLOW (AUTH OR DELETE)                             */}
          {/* ========================================================================= */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            {/* If Confirmed Deletion is Active */}
            {confirmDeletion ? (
              <div className="bg-white rounded-3xl border border-[#DDE4ED] shadow-xl p-8 sm:p-10 text-center animate-in fade-in zoom-in-95 duration-200">
                <div className="w-28 h-28 mx-auto mb-6 bg-emerald-50 rounded-full flex items-center justify-center p-4 border-4 border-emerald-100">
                  <img
                    src="https://assets.learntube.ai/files/Profile-section/task-done.svg?updatedAt=1690268616774"
                    alt="Account Deleted"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0E1B2E]">
                  Account Successfully Deleted
                </h2>
                <p className="text-[#4A5A70] text-sm sm:text-base mt-2 max-w-md mx-auto">
                  Your profile and personal records have been queued for permanent erasure. Thank you for learning with us.
                </p>

                <div className="mt-8 p-4 bg-[#F8FAFC] rounded-2xl border border-[#DDE4ED] inline-flex items-center gap-2 text-sm font-semibold text-[#086788]">
                  <span>Redirecting to courses in {countDown}s</span>
                  <span className="delete-dot-1">.</span>
                  <span className="delete-dot-2">.</span>
                  <span className="delete-dot-3">.</span>
                </div>

                <div className="mt-6">
                  <button
                    type="button"
                    onClick={performLogoutRedirect}
                    className="text-xs font-bold text-[#4A5A70] hover:text-[#0E1B2E] underline cursor-pointer"
                  >
                    Click here if not redirected automatically
                  </button>
                </div>
              </div>
            ) : isLoggedIn ? (
              /* ===================================================================== */
              /* STATE 1: USER IS LOGGED IN -> DISPLAY DELETION FUNCTIONALITY (Modal UI) */
              /* ===================================================================== */
              <div className="bg-white rounded-3xl border border-rose-200/90 shadow-[0_16px_40px_-12px_rgba(224,96,100,0.15)] overflow-hidden">
                {/* Danger Zone Header Banner */}
                <div className="bg-gradient-to-r from-rose-500 via-[#E06064] to-[#C9474B] p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-xs flex items-center justify-center text-white">
                        <Trash2 size={22} />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold leading-tight">Delete Account Confirmation</h2>
                        <p className="text-white/80 text-xs mt-0.5">Permanent erasure of credentials and data</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-md bg-white/20 text-xs font-bold uppercase tracking-wider">
                      Danger Zone
                    </span>
                  </div>
                </div>

                <div className="p-6 sm:p-8 flex flex-col gap-6">
                  {/* Account In Question Badge */}
                  <div className="flex items-center justify-between p-4 bg-[#F8FAFC] border border-[#DDE4ED] rounded-2xl">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-[#086788] text-white flex items-center justify-center font-bold text-base shadow-xs">
                        {userDisplayName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#78899F] uppercase tracking-wider">Currently Signed In</p>
                        <h3 className="text-sm sm:text-base font-bold text-[#0E1B2E]">{userDisplayName}</h3>
                        <p className="text-xs text-[#4A5A70]">{userEmail}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="text-xs font-bold text-[#086788] hover:text-[#064e67] hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      <LogOut size={13} />
                      Switch Account
                    </button>
                  </div>

                  {/* Illustration & Warning Copy */}
                  <div className="flex flex-col sm:flex-row items-center gap-6 p-4 rounded-2xl bg-rose-50/70 border border-rose-200">
                    <div className="w-24 h-24 shrink-0">
                      <img
                        src="https://assets.learntube.ai/files/Profile-section/delete-user.svg?updatedAt=1690268616824"
                        alt="Delete Account Alert"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-rose-900 leading-snug">
                        Are you sure you want to delete your LearnTube account?
                      </h4>
                      <p className="text-xs sm:text-sm text-rose-700 mt-1 leading-relaxed">
                        If you proceed, your account cannot be recovered. You will permanently forfeit all course certifications, assessment records, and any remaining pro credits.
                      </p>
                    </div>
                  </div>

                  {/* Impact Checklist */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#DDE4ED] flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                        <X size={12} />
                      </div>
                      <span className="text-[#4A5A70] leading-snug">
                        <strong className="text-[#0E1B2E] block">All Certificates Voided</strong>
                        Verification links will instantly become invalid.
                      </span>
                    </div>

                    <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#DDE4ED] flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                        <X size={12} />
                      </div>
                      <span className="text-[#4A5A70] leading-snug">
                        <strong className="text-[#0E1B2E] block">Learning Progress Erased</strong>
                        Completed modules, streaks, and quiz results deleted.
                      </span>
                    </div>

                    <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#DDE4ED] flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                        <X size={12} />
                      </div>
                      <span className="text-[#4A5A70] leading-snug">
                        <strong className="text-[#0E1B2E] block">Forfeiture of Pro Access</strong>
                        Active subscription time & coins cancelled without refund.
                      </span>
                    </div>

                    <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#DDE4ED] flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                        <X size={12} />
                      </div>
                      <span className="text-[#4A5A70] leading-snug">
                        <strong className="text-[#0E1B2E] block">Identity Purged</strong>
                        Login credentials expunged under GDPR privacy terms.
                      </span>
                    </div>
                  </div>

                  {/* Confirmation Keyword Input */}
                  <div className="w-full flex flex-col justify-center items-start">
                    <div className="text-[14px] font-medium text-[#0E1B2E] mb-1.5">
                      To confirm, type <span className="font-bold text-[#E06064]">Delete</span> in the box below
                      <span className="font-bold text-[#E06064] ml-0.5">*</span>
                    </div>
                    <div className="relative w-full">
                      <input
                        type="text"
                        required
                        placeholder='Type "Delete"'
                        value={confirmationKeyword}
                        onChange={(e) => setConfirmationKeyword(e.target.value)}
                        className="w-full h-11 px-3.5 rounded-xl border-2 border-rose-300 text-sm font-semibold text-[#0E1B2E] focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-200 bg-white transition-all uppercase tracking-wider"
                      />
                      {confirmationKeyword.trim().toLowerCase() === "delete" && (
                        <span className="absolute right-3.5 top-3 text-emerald-600 font-bold text-xs flex items-center gap-1">
                          <Check size={16} /> Ready
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Reason for Deletion */}
                  <div className="w-full flex flex-col justify-center items-start">
                    <div className="text-[14px] font-medium text-[#0E1B2E] mb-1.5 flex items-center justify-between w-full">
                      <span>Reason for deletion</span>
                      <span className="text-[11px] text-[#78899F]">Click a chip or type below</span>
                    </div>

                    {/* Quick reason suggestions */}
                    <div className="flex flex-wrap gap-1.5 mb-2.5 w-full">
                      {DELETION_REASONS.map((r) => {
                        const isSelected = deletion_reason === r;
                        return (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setDeletion_reason(r)}
                            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer ${isSelected
                              ? "bg-rose-50 border-rose-400 text-rose-900 font-bold shadow-xs"
                              : "bg-white border-[#DDE4ED] text-[#4A5A70] hover:bg-[#F8FAFC]"
                              }`}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>

                    <input
                      type="text"
                      name="deletion_reason"
                      placeholder="Reason for deletion"
                      value={deletion_reason}
                      onChange={(e) => setDeletion_reason(e.target.value)}
                      className="w-full h-11 px-3.5 rounded-xl border-2 border-[#CBD5E1] text-sm text-[#0E1B2E] focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-200 bg-white transition-all"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                    <button
                      type="button"
                      disabled={
                        isDeleting || confirmationKeyword.trim().toLowerCase() !== "delete"
                      }
                      onClick={handleDeleteAccount}
                      className="w-full sm:w-1/2 h-12 rounded-xl bg-[#E06064] hover:bg-[#d5484c] text-white font-bold text-sm sm:text-base flex items-center justify-center gap-2 shadow-[0_4px_14px_rgba(224,96,100,0.35)] transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {isDeleting ? (
                        <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      ) : (
                        <>
                          <Trash2 size={17} />
                          <span>Delete My Account</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate("/my-profile/user")}
                      className="w-full sm:w-1/2 h-12 rounded-xl border border-[#CBD5E1] bg-white hover:bg-[#F8FAFC] text-[#4A5A70] font-bold text-sm sm:text-base transition-colors cursor-pointer"
                    >
                      Cancel & Keep Account
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ===================================================================== */
              /* STATE 2: USER NOT LOGGED IN -> DISPLAY LOGIN / SIGNUP (Login.jsx UI) */
              /* ===================================================================== */
              <div className="bg-white rounded-3xl border border-[#DDE4ED] shadow-xl p-6 sm:p-9 relative">
                {/* Brand Badge */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#EDF1F7]">
                  <div className="flex items-center gap-2.5">
                    <div className="w-[30px] h-[30px] rounded-[8px] bg-[#203C5C] flex items-center justify-center overflow-hidden">
                      <img
                        src="https://assets.learntube.ai/files/new%20Linkedin%20data/image%2011.png?updatedAt=1747309992449"
                        alt="LearnTube"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <span className="font-bold text-[17px] text-[#0E1B2E] tracking-tight">
                      LearnTube.ai
                    </span>
                  </div>

                  {/* Mode Pill Toggle */}
                  <div className="flex items-center bg-[#EDF1F7] p-1 rounded-full text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => setAuthMode("login")}
                      className={`px-3 py-1.5 rounded-full transition-all cursor-pointer ${authMode === "login"
                        ? "bg-white text-[#0E1B2E] shadow-xs"
                        : "text-[#78899F] hover:text-[#0E1B2E]"
                        }`}
                    >
                      Sign In
                    </button>
                    <button
                      type="button"
                      onClick={() => setAuthMode("signup")}
                      className={`px-3 py-1.5 rounded-full transition-all cursor-pointer ${authMode === "signup"
                        ? "bg-white text-[#0E1B2E] shadow-xs"
                        : "text-[#78899F] hover:text-[#0E1B2E]"
                        }`}
                    >
                      Sign Up
                    </button>
                  </div>
                </div>

                {/* Authentication Intro */}
                <div className="mb-6">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 mb-2">
                    <Lock size={12} />
                    Authentication Required
                  </div>
                  <h2 className="text-2xl font-bold text-[#0E1B2E] tracking-tight">
                    {authMode === "signup"
                      ? "Create an account or identify yourself"
                      : "Sign in to access account deletion"}
                  </h2>
                  <p className="text-xs sm:text-sm text-[#4A5A70] mt-1 leading-relaxed">
                    To safeguard user data and prevent unauthorized requests, you must authenticate to verify ownership of the account you wish to delete.
                  </p>
                </div>

                {/* Google Sign-in */}
                <GoogleLogin
                  clientId={XANO_GOOGLE_CLIENT_ID}
                  render={(renderProps) => (
                    <button
                      type="button"
                      onClick={renderProps.onClick}
                      disabled={renderProps.disabled || isLoadingAuth}
                      className="w-full h-[50px] rounded-full text-white font-bold text-[15px] sm:text-[16px] flex items-center justify-center gap-2.5 shadow-[0_3px_12px_rgba(8,103,136,0.25)] hover:opacity-95 active:scale-[0.99] transition-all cursor-pointer disabled:opacity-50"
                      style={{
                        background:
                          "linear-gradient(121.35deg, #13A0BD 16.15%, #086788 86.12%)",
                      }}
                    >
                      <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center shrink-0">
                        <svg viewBox="0 0 24 24" width="18" height="18">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                          />
                        </svg>
                      </div>
                      <span>
                        {authMode === "signup"
                          ? "Continue with Google"
                          : "Sign in with Google"}
                      </span>
                    </button>
                  )}
                  onSuccess={handleGoogleAuthResponse}
                  onFailure={handleGoogleAuthResponse}
                  cookiePolicy={"single_host_origin"}
                />

                {/* Divider */}
                <div className="flex items-center my-5">
                  <div className="flex-1 h-[1px] bg-[#DDE4ED]" />
                  <span className="px-3 text-[12px] font-semibold text-[#78899F]">
                    {authMode === "signup"
                      ? "or register with email"
                      : "or continue with email"}
                  </span>
                  <div className="flex-1 h-[1px] bg-[#DDE4ED]" />
                </div>

                {/* Forms */}
                {authMode === "signup" ? (
                  /* =============================================================== */
                  /* SIGNUP FORM                                                     */
                  /* =============================================================== */
                  <form onSubmit={handleSignupSubmit} className="flex flex-col gap-3.5">
                    <div>
                      <label className="text-[13px] font-semibold text-[#0E1B2E] mb-1.5 block">
                        Your Name<span className="text-[#E06064]">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="For e.g., John Doe"
                        value={signupData.name}
                        onChange={(e) =>
                          setSignupData({ ...signupData, name: e.target.value })
                        }
                        className="h-[44px] w-full rounded-[10px] border border-[#CBD5E1] px-3.5 text-[14px] font-medium text-[#0E1B2E] focus:outline-none focus:border-[#13A0BD] focus:ring-1 focus:ring-[#13A0BD] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="text-[13px] font-semibold text-[#0E1B2E] mb-1.5 block">
                        Email<span className="text-[#E06064]">*</span>
                        {isEmailError && (
                          <span className="ml-2 text-[12px] font-normal italic text-[#E06064]">
                            Already in use, try to login
                          </span>
                        )}
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="For e.g., johndoe123@gmail.com"
                        value={signupData.email}
                        onChange={(e) => {
                          setIsEmailError(false);
                          setSignupData({ ...signupData, email: e.target.value });
                        }}
                        className={`h-[44px] w-full rounded-[10px] border px-3.5 text-[14px] font-medium text-[#0E1B2E] focus:outline-none focus:border-[#13A0BD] focus:ring-1 focus:ring-[#13A0BD] transition-colors ${isEmailError ? "border-[#E06064]" : "border-[#CBD5E1]"
                          }`}
                      />
                    </div>

                    <div>
                      <label className="text-[13px] font-semibold text-[#0E1B2E] mb-1.5 block">
                        Password<span className="text-[#E06064]">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          required
                          placeholder="Create a password"
                          value={signupData.password}
                          onChange={(e) =>
                            setSignupData({ ...signupData, password: e.target.value })
                          }
                          className="h-[44px] w-full rounded-[10px] border border-[#CBD5E1] pl-3.5 pr-10 text-[14px] font-medium text-[#0E1B2E] focus:outline-none focus:border-[#13A0BD] focus:ring-1 focus:ring-[#13A0BD] transition-colors"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-3 flex items-center text-[#78899F] hover:text-[#0E1B2E]"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoadingAuth}
                      className="mt-2 h-[48px] w-full rounded-full border-[1.5px] border-[#13A0BD] text-[#086788] font-bold text-[15px] flex items-center justify-center gap-2 hover:bg-[#E6F4F8] active:scale-[0.99] transition-all cursor-pointer disabled:opacity-50"
                    >
                      <span>{isLoadingAuth ? "Signing up..." : "Sign Up & Continue"}</span>
                      <ArrowRight size={16} />
                    </button>

                    <div className="mt-2 text-center text-xs text-[#4A5A70]">
                      Already have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setAuthMode("login")}
                        className="text-[#06769E] font-bold hover:underline cursor-pointer"
                      >
                        Log in here
                      </button>
                    </div>
                  </form>
                ) : (
                  /* =============================================================== */
                  /* LOGIN FORM                                                      */
                  /* =============================================================== */
                  <form onSubmit={handleLoginSubmit} className="flex flex-col gap-3.5">
                    <div>
                      <label className="text-[13px] font-semibold text-[#0E1B2E] mb-1.5 block">
                        Email Address<span className="text-[#E06064]">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="For e.g., johndoe123@gmail.com"
                        value={loginData.email}
                        onChange={(e) =>
                          setLoginData({ ...loginData, email: e.target.value })
                        }
                        className="h-[44px] w-full rounded-[10px] border border-[#CBD5E1] px-3.5 text-[14px] font-medium text-[#0E1B2E] focus:outline-none focus:border-[#13A0BD] focus:ring-1 focus:ring-[#13A0BD] transition-colors"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-[13px] font-semibold text-[#0E1B2E]">
                          Password<span className="text-[#E06064]">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsPasswordResetModalEnabled(true)}
                          className="text-[12px] font-semibold text-[#06769E] hover:underline cursor-pointer"
                        >
                          Forgot Password?
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          required
                          placeholder="Enter your password"
                          value={loginData.password}
                          onChange={(e) =>
                            setLoginData({ ...loginData, password: e.target.value })
                          }
                          className="h-[44px] w-full rounded-[10px] border border-[#CBD5E1] pl-3.5 pr-10 text-[14px] font-medium text-[#0E1B2E] focus:outline-none focus:border-[#13A0BD] focus:ring-1 focus:ring-[#13A0BD] transition-colors"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-3 flex items-center text-[#78899F] hover:text-[#0E1B2E]"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoadingAuth}
                      className="mt-2 h-[48px] w-full rounded-full border-[1.5px] border-[#13A0BD] text-[#086788] font-bold text-[15px] flex items-center justify-center gap-2 hover:bg-[#E6F4F8] active:scale-[0.99] transition-all cursor-pointer disabled:opacity-50"
                    >
                      <span>{isLoadingAuth ? "Verifying..." : "Verify & Continue"}</span>
                      <ArrowRight size={16} />
                    </button>

                    <div className="mt-2 text-center text-xs text-[#4A5A70]">
                      Need an account first?{" "}
                      <button
                        type="button"
                        onClick={() => setAuthMode("signup")}
                        className="text-[#06769E] font-bold hover:underline cursor-pointer"
                      >
                        Sign up here
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* ========================================================================= */}
          {/* RIGHT COLUMN: TERMS, POLICIES & DATA RETENTION OVERVIEW                   */}
          {/* ========================================================================= */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Policy Reference Cards */}
            <div className="bg-white rounded-3xl border border-[#DDE4ED] shadow-sm p-6 sm:p-7">
              <div className="flex items-center gap-2.5 mb-4">
                <FileText className="text-[#086788]" size={20} />
                <h3 className="text-base font-bold text-[#0E1B2E]">
                  Official Policies & Terms
                </h3>
              </div>
              <p className="text-xs text-[#4A5A70] mb-4 leading-relaxed">
                LearnTube (operated by BeNinja Solutions Pvt. Ltd.) processes account modifications and deletions according to our legal agreements:
              </p>

              {/* Policy Links List */}
              <div className="flex flex-col gap-2.5">
                <Link
                  to="/privacypolicy"
                  className="p-3 rounded-2xl bg-[#F8FAFC] hover:bg-[#EDF1F7] border border-[#DDE4ED] flex items-center justify-between transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-cyan-100 text-[#086788] flex items-center justify-center font-bold text-xs">
                      §10
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#0E1B2E] group-hover:text-[#086788]">
                        Privacy Policy
                      </h4>
                      <p className="text-[11px] text-[#78899F]">
                        Section 10: Updating & Deleting Personal Information
                      </p>
                    </div>
                  </div>
                  <ExternalLink size={14} className="text-[#78899F] group-hover:text-[#086788]" />
                </Link>

                <Link
                  to="/termsandconditions"
                  className="p-3 rounded-2xl bg-[#F8FAFC] hover:bg-[#EDF1F7] border border-[#DDE4ED] flex items-center justify-between transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">
                      T&C
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#0E1B2E] group-hover:text-[#086788]">
                        Terms & Conditions
                      </h4>
                      <p className="text-[11px] text-[#78899F]">
                        Platform obligations, termination, and service scope
                      </p>
                    </div>
                  </div>
                  <ExternalLink size={14} className="text-[#78899F] group-hover:text-[#086788]" />
                </Link>

                <Link
                  to="/refundpolicy"
                  className="p-3 rounded-2xl bg-[#F8FAFC] hover:bg-[#EDF1F7] border border-[#DDE4ED] flex items-center justify-between transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center font-bold text-xs">
                      REF
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#0E1B2E] group-hover:text-[#086788]">
                        Refunds & Cancellations
                      </h4>
                      <p className="text-[11px] text-[#78899F]">
                        LearnTube Pro, X, and Max subscription validity rules
                      </p>
                    </div>
                  </div>
                  <ExternalLink size={14} className="text-[#78899F] group-hover:text-[#086788]" />
                </Link>
              </div>
            </div>

            {/* Interactive FAQs Accordion */}
            <div className="bg-white rounded-3xl border border-[#DDE4ED] shadow-sm p-6 sm:p-7">
              <div className="flex items-center gap-2.5 mb-4">
                <HelpCircle className="text-[#086788]" size={20} />
                <h3 className="text-base font-bold text-[#0E1B2E]">
                  Frequently Asked Questions
                </h3>
              </div>

              <div className="flex flex-col gap-2.5">
                {POLICY_FAQS.map((faq, idx) => {
                  const isOpen = activeFaq === idx;
                  return (
                    <div
                      key={faq.question}
                      className="border border-[#E2E8F0] rounded-2xl overflow-hidden transition-all"
                    >
                      <button
                        type="button"
                        onClick={() => setActiveFaq(isOpen ? null : idx)}
                        className="w-full text-left p-3.5 flex items-center justify-between gap-2 text-xs sm:text-sm font-semibold text-[#0E1B2E] hover:bg-[#F8FAFC] transition-colors cursor-pointer"
                      >
                        <span>{faq.question}</span>
                        {isOpen ? (
                          <ChevronUp size={16} className="text-[#78899F] shrink-0" />
                        ) : (
                          <ChevronDown size={16} className="text-[#78899F] shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-3.5 pb-3.5 pt-1 text-xs text-[#4A5A70] leading-relaxed bg-[#F8FAFC]/50 border-t border-[#F1F5F9]">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Support Lifeline Card */}
            <div className="p-6 bg-gradient-to-br from-[#086788] to-[#043E54] rounded-3xl text-white shadow-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
                  <LifeBuoy size={20} className="text-cyan-300" />
                </div>
                <div>
                  <h4 className="font-bold text-sm sm:text-base text-white">Need help before deleting?</h4>
                  <p className="text-xs text-white/80 mt-1 leading-relaxed">
                    If you are experiencing course access issues, billing confusion, or wish to export your certificates first, our support team is here to assist.
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-2 text-xs">
                <a
                  href="mailto:hello@careerninja.in?subject=Help%20with%20my%20LearnTube%20Account"
                  className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white font-semibold transition-colors flex items-center gap-1.5"
                >
                  <MessageCircle size={13} />
                  Email Support
                </a>
                <a
                  href={allFooterLinks.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white font-semibold transition-colors flex items-center gap-1.5"
                >
                  WhatsApp Assistant
                </a>
                <Link
                  to="/contactus"
                  className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white font-semibold transition-colors flex items-center gap-1.5"
                >
                  Contact Form
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Password Reset Modal from Login.jsx */}
      <PasswordResetModal
        isModalEnabled={isPasswordResetModalEnabled}
        setIsModalEnabled={setIsPasswordResetModalEnabled}
      />
    </div>
  );
}
